import React, { createContext, useContext, useState, ReactNode, useCallback } from 'react';
import { Snackbar, Alert, AlertColor, AlertProps, AlertTitle, LinearProgress, Box } from '@mui/material';

interface NotifierVariantProps extends AlertProps {
    duration?: number;
    title?: string;
    action?: ReactNode;
    icon?: boolean;
}

interface NotificationState {
    open: boolean;
    message: string;
    severity: AlertColor;
    autoHideDuration: number;
    icon?: boolean;
    props?: NotifierVariantProps;
}

interface NotifierContextType {
    showError: (message: string, props?: NotifierVariantProps) => void;
    showInfo: (message: string, props?: NotifierVariantProps) => void;
    showSuccess: (message: string, props?: NotifierVariantProps) => void;
    showWarning: (message: string, props?: NotifierVariantProps) => void;
}

const NotifierContext = createContext<NotifierContextType | undefined>(undefined);

const DEFAULT_DURATION = 5000;

interface NotifierProviderProps {
    children: ReactNode;
}

export const NotifierProvider: React.FC<NotifierProviderProps> = ({ children }) => {
    const [notification, setNotification] = useState<NotificationState>({
        open: false,
        message: '',
        severity: 'info',
        autoHideDuration: DEFAULT_DURATION,
        icon: true,
    });

    const showNotification = useCallback((message: string, severity: AlertColor, props?: NotifierVariantProps) => {
        setNotification({
            open: true,
            message,
            severity,
            autoHideDuration: props?.duration ?? DEFAULT_DURATION,
            icon: props?.icon === false ? false : true,
            props
        });
    }, []);

    const handleClose = useCallback((_event?: React.SyntheticEvent | Event, reason?: string) => {
        if (reason === 'clickaway') {
            return;
        }
        setNotification(prev => ({ ...prev, open: false }));
    }, []);

    const showError = useCallback((message: string, props?: NotifierVariantProps) => {
        showNotification(message, 'error', props);
    }, [showNotification]);

    const showInfo = useCallback((message: string, props?: NotifierVariantProps) => {
        showNotification(message, 'info', props);
    }, [showNotification]);

    const showSuccess = useCallback((message: string, props?: NotifierVariantProps) => {
        showNotification(message, 'success', props);
    }, [showNotification]);

    const showWarning = useCallback((message: string, props?: NotifierVariantProps) => {
        showNotification(message, 'warning', props);
    }, [showNotification]);

    const value: NotifierContextType = {
        showError,
        showInfo,
        showSuccess,
        showWarning,
    };

    return (
        <NotifierContext.Provider value={value}>
            {children}
            <Snackbar
                open={notification.open}
                autoHideDuration={notification.autoHideDuration}
                onClose={handleClose}
                anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
            >
                <Alert
                    onClose={handleClose}
                    severity={notification.severity}
                    // Make sure the icon is always true if the icon prop is other that false
                    // icon={notification.icon === false ? false : true}
                    {...(notification.icon === false ? {icon: false} : {})}
                    variant={'standard'}
                    sx={{
                        width: '100%',
                        fontSize: 'var(--font-size-md)',
                        position: 'relative',
                        overflow: 'hidden',
                    }}
                >
                    {notification.props?.title ? <AlertTitle>{notification.props?.title}</AlertTitle> : null}
                    {notification.message}
                    {notification.props?.action ? <Box sx={{ marginTop: '15px' }}>{notification.props?.action}</Box> : null}

                    <LinearProgress
                        variant={notification.severity}
                        sx={{
                            position: 'absolute',
                            bottom: 0,
                            left: 0,
                            right: 0,
                        }}
                    />
                </Alert>
            </Snackbar>
        </NotifierContext.Provider>
    );
};

export const useNotifierContext = (): NotifierContextType => {
    const context = useContext(NotifierContext);
    if (context === undefined) {
        throw new Error('useNotifierContext must be used within a NotifierProvider');
    }
    return context;
};

export default NotifierProvider;

